import BigNumber from 'bignumber.js'
import { useMemo, CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { TradeDirection } from '~/components/Exchange/types'
import { useTokenPrice } from '~/hooks/useTokenPrice'
import {
  updateLimitSwapAmount,
  updateLimitSwapPairAmount,
  updateLimitSwapLimitPrice,
} from '~/state/dex/actions'
import {
  useInputAmountVerify,
  useLimitSwapAmount,
  useLimitSwapPair,
  useLimitSwapLimitPrice,
} from '~/state/dex/hooks'
import { useAppDispatch } from '~/state/hooks'
import { Currency, FixedPointNumber } from '~/utils/fixedPoint'
import { SpreadLabelLite } from '../SpreadLabel'
import TokenSelect from '../TokenSelect'
import {
  Input,
  InputGroup,
  InputHelpText,
  InputRow,
  InputWrapper,
} from './styled'
import { formatFormAmount, toBigNumber } from '~/utils/numbers'
import { sanitizeInputValue, isValidDecimal } from '../../utils'

interface TokenInputProps {
  direction: TradeDirection
  groupStyle?: CSSProperties
}

const TokenInput: React.FC<TokenInputProps> = ({
  direction,
  groupStyle,
}: TokenInputProps) => {
  const { t } = useTranslation()
  const [inputToken, outputToken] = useLimitSwapPair()
  const { inputAmount, outputAmount } = useLimitSwapAmount()
  const limitPrice = useLimitSwapLimitPrice()

  const isInputDirection = TradeDirection.Input === direction
  const token = isInputDirection ? inputToken : outputToken
  const exchangeAmount = isInputDirection ? inputAmount : outputAmount
  const tokenUnitPrice = useTokenPrice(token?.address)
  const exchangeAmountLiteral = useMemo(() => {
    // @notice: exchangeAmount may be in scientific notation
    const transferAmount = toBigNumber(exchangeAmount)
    const [_, decimal] = transferAmount.toFixed().split('.')
    if (decimal && decimal.length > 6 && !isInputDirection)
      return formatFormAmount(transferAmount)
    return exchangeAmount.toString()
  }, [exchangeAmount])

  const verifyInputAmount = useInputAmountVerify()
  const dispatch = useAppDispatch()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = sanitizeInputValue(e.target.value)

    if (!isValidDecimal(value)) return
    if (!verifyInputAmount(value) && value !== '') return

    if (value === '') {
      dispatch(updateLimitSwapPairAmount({ inputAmount: '', outputAmount: '' }))
      return
    }

    if (limitPrice) {
      const calcOutputAmount = new BigNumber(value)
        .times(limitPrice)
        .decimalPlaces(outputToken?.decimals)
        .toString()

      dispatch(
        updateLimitSwapPairAmount({
          inputAmount: value,
          outputAmount: calcOutputAmount,
        }),
      )
      return
    }

    dispatch(updateLimitSwapAmount({ direction, amount: value }))
  }

  const handleOutputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = sanitizeInputValue(e.target.value)

    if (!isValidDecimal(value)) return
    if (!verifyInputAmount(value) && value !== '') return

    if (value === '') {
      dispatch(updateLimitSwapLimitPrice(''))
      dispatch(updateLimitSwapAmount({ direction, amount: value }))
      return
    }

    if (inputAmount) {
      const calcLimitPrice = new BigNumber(value).div(inputAmount)
      const calcScale = +calcLimitPrice < 1e-6 ? 12 : 6
      const newLimitPrice = calcLimitPrice.decimalPlaces(calcScale).toFixed()

      dispatch(updateLimitSwapLimitPrice(newLimitPrice))
      dispatch(updateLimitSwapAmount({ direction, amount: value }))
      return
    }

    if (!inputAmount && limitPrice) {
      const calcInputAmount = new BigNumber(value)
        .div(limitPrice)
        .decimalPlaces(inputToken?.decimals)
        .toString()

      dispatch(
        updateLimitSwapPairAmount({
          inputAmount: calcInputAmount,
          outputAmount: value,
        }),
      )
      return
    }

    dispatch(updateLimitSwapAmount({ direction, amount: value }))
  }

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    isInputDirection ? handleInputChange(e) : handleOutputChange(e)

  const onInputFocus = () => {
    if (!exchangeAmount) return

    const amount = new BigNumber(exchangeAmountLiteral)
      .decimalPlaces(6)
      .toString()
    dispatch(
      updateLimitSwapAmount({
        direction,
        amount,
      }),
    )
  }

  const usdValue = useMemo(() => {
    return new BigNumber(exchangeAmount).times(tokenUnitPrice)
  }, [exchangeAmount, tokenUnitPrice])

  /**
   * 用户没有输入数量的时候不展示价值，输入数量 0 展示价值 0
   */
  const usdValueLiteral =
    Number(exchangeAmount) === 0
      ? '$0'
      : Number(usdValue)
      ? `~${new FixedPointNumber(usdValue).toCurrency(Currency.USD)}`
      : t('rate_unavailable')

  const placeholder = isInputDirection
    ? t('amount_for_sell')
    : t('amount_for_buy')

  return (
    <InputGroup style={groupStyle}>
      <InputWrapper>
        <TokenSelect direction={direction}></TokenSelect>
        <InputRow>
          <Input
            inputMode="decimal"
            onChange={handleAmountChange}
            onFocus={onInputFocus}
            placeholder={placeholder}
            value={exchangeAmountLiteral}
          ></Input>
          {!!exchangeAmount && (
            <InputHelpText>
              {usdValueLiteral}
              <SpreadLabelLite direction={direction} />
            </InputHelpText>
          )}
        </InputRow>
      </InputWrapper>
    </InputGroup>
  )
}

export default TokenInput
